/* Forge & Flame reskin — product tabs, without jQuery UI.
 *
 * WHY THIS REPLACES jQuery UI
 * ---------------------------
 * The PDP tab strip rendered only intermittently. Root cause: the theme loads
 * jQuery TWICE, and the two loads race.
 *
 *   layout/theme.liquid:55   jQuery 3.6.4  (defer)
 *   layout/theme.liquid:307  jQuery 3.5.1  (NOT deferred — a dependency of the
 *                                           Custom Price Calculator app)
 *
 * jQuery UI extends whichever instance is `window.jQuery` at the moment it
 * executes. When the other jQuery loads afterwards it replaces the global, and
 * the extended copy — along with `$.fn.tabs` — is discarded. Measured on a
 * loaded PDP: `window.jQuery === window.$`, version 3.6.4, `$.ui` undefined and
 * `$.fn.tabs` not a function, even on page loads where the tabs DID initialise.
 *
 * That makes the failure unfixable by waiting or retrying: on a bad load
 * `.tabs` never appears on the surviving global at all. Removing the duplicate
 * jQuery is not an option either — it belongs to a third-party app.
 *
 * So the tabs no longer depend on jQuery. This emits the same `ui-*` class
 * names jQuery UI would, so `assets/section-product-tabs.css.liquid` and the
 * reskin's tab styling keep working untouched.
 *
 * It is idempotent and defensive: if jQuery UI did win the race and already
 * initialised the widget, this leaves it alone.
 */
(function () {
  'use strict';

  var CONTAINER = 'ui-tabs ui-corner-all ui-widget ui-widget-content';
  var NAV = 'ui-tabs-nav ui-corner-all ui-helper-reset ui-helper-clearfix ui-widget-header';
  var TAB = 'ui-tabs-tab ui-corner-top ui-state-default ui-tab';
  var PANEL = 'ui-tabs-panel ui-corner-bottom ui-widget-content';

  function activate(root, index) {
    var tabs = root.__ffTabs;
    var panels = root.__ffPanels;

    tabs.forEach(function (li, i) {
      var on = i === index;
      li.classList.toggle('ui-tabs-active', on);
      li.classList.toggle('ui-state-active', on);
      li.setAttribute('aria-selected', on ? 'true' : 'false');
      var a = li.querySelector('a');
      if (a) a.setAttribute('tabindex', on ? '0' : '-1');
    });

    panels.forEach(function (panel, i) {
      var on = i === index;
      panel.hidden = !on;
      panel.style.display = on ? '' : 'none';
    });
  }

  function init(root) {
    if (!root || root.__ffTabsReady) return;
    // jQuery UI got there first — leave its widget alone.
    if (root.classList.contains('ui-tabs')) return;

    var nav = root.querySelector('ul');
    if (!nav) return;

    var tabs = Array.prototype.slice.call(nav.children).filter(function (li) {
      return li.tagName === 'LI' && li.querySelector('a[href^="#"]');
    });
    if (!tabs.length) return;

    var panels = tabs
      .map(function (li) {
        var id = li.querySelector('a[href^="#"]').getAttribute('href').slice(1);
        return document.getElementById(id);
      })
      .filter(Boolean);
    if (panels.length !== tabs.length) return; // markup we don't understand — bail

    root.__ffTabs = tabs;
    root.__ffPanels = panels;

    root.className = (root.className + ' ' + CONTAINER).trim();
    nav.className = (nav.className + ' ' + NAV).trim();
    nav.setAttribute('role', 'tablist');

    tabs.forEach(function (li, i) {
      li.className = (li.className + ' ' + TAB).trim();
      li.setAttribute('role', 'tab');
      var a = li.querySelector('a');
      a.classList.add('ui-tabs-anchor');
      a.setAttribute('role', 'presentation');

      a.addEventListener('click', function (e) {
        e.preventDefault();
        activate(root, i);
      });

      li.addEventListener('keydown', function (e) {
        var next = null;
        if (e.key === 'ArrowRight') next = (i + 1) % tabs.length;
        else if (e.key === 'ArrowLeft') next = (i - 1 + tabs.length) % tabs.length;
        else if (e.key === 'Home') next = 0;
        else if (e.key === 'End') next = tabs.length - 1;
        if (next === null) return;
        e.preventDefault();
        activate(root, next);
        var link = tabs[next].querySelector('a');
        if (link) link.focus();
      });
    });

    panels.forEach(function (panel) {
      panel.className = (panel.className + ' ' + PANEL).trim();
      panel.setAttribute('role', 'tabpanel');
    });

    activate(root, 0);
    root.__ffTabsReady = true;
  }

  function boot() {
    init(document.getElementById('tabs'));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  // Second pass after load, in case the markup is injected late.
  window.addEventListener('load', boot);
})();
